import React, { useEffect, useMemo, useState } from "react";
import { CloseButton, Offcanvas, Spinner } from "react-bootstrap";
import { Button as AntButton, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import CopyableText from "components/common/CopyableText";
import useFormattedDate from "hooks/useFormatDate";
import { useAuth } from "contexts/AuthContext";
import { toast } from "react-toastify";

/* ============================= */
/* STATUS STYLES (unchanged)     */
/* ============================= */

const STATUS_STYLES = {
  ACTIVE: { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" },
  NEW: { bg: "#e3f2fd", text: "#1565c0", border: "#bbdefb" },
  BLOCKED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
  SUSPENDED: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
  INACTIVE: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
  RATE_LIMITED: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
  THROTTLED: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
  DELETED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
  DEFAULT: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
};

/* ============================= */
/* SMALL UI COMPONENTS           */
/* ============================= */

const SectionLabel = ({ children }) => (
  <div
    className="text-600 text-uppercase fw-semibold mb-2"
    style={{ fontSize: 11, letterSpacing: 0.6 }}
  >
    {children}
  </div>
);

const DetailItem = ({ label, children }) => (
  <div className="mb-3">
    <SectionLabel>{label}</SectionLabel>
    <div
      className="rounded-3 px-3 py-2 text-700"
      style={{
        fontSize: 13,
        wordBreak: "break-word",
        backgroundColor: "#f5f7fa",
      }}
    >
      {children || "--"}
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const normalized = String(status || "UNKNOWN").toUpperCase();
  const ui = STATUS_STYLES[normalized] || STATUS_STYLES.DEFAULT;
  const label = normalized.replaceAll("_", " ");

  return (
    <span
      style={{
        backgroundColor: ui.bg,
        color: ui.text,
        border: `1px solid ${ui.border}`,
        borderRadius: 999,
        padding: "5px 12px",
        fontSize: 11,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: ui.text,
          animation: "statusDotBlink 2.2s ease-in-out infinite",
        }}
      />
      {label}
    </span>
  );
};

const getStatusPanelStyle = (status) => {
  const normalized = String(status || "UNKNOWN").toUpperCase();

  // Match Health Check diagnostics green treatment for ACTIVE.
  if (normalized === "ACTIVE") {
    return {
      bg: "#f3fbf6",
      border: "#d1f2e0",
      heading: "#166534",
    };
  }

  const ui = STATUS_STYLES[normalized] || STATUS_STYLES.DEFAULT;
  return {
    bg: ui.bg,
    border: ui.border,
    heading: ui.text,
  };
};

/* ============================= */
/* MAIN DRAWER                   */
/* ============================= */

export const ClientIDDetailsDrawer = ({
  selectedID,
  loading,
  showDrawer,
  setShowDrawer,
  startInEditMode = false,
  updateClientID,
  onRequestEdit,
  onExitEdit,
}) => {
  const [visible, setVisible] = useState(false);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  const { user } = useAuth();
  const { formatDateTime } = useFormattedDate();

  /* ============================= */
  /* Effects                       */
  /* ============================= */

  useEffect(() => {
    if (showDrawer) {
      setVisible(true);
      setIsEditingMode(startInEditMode);
    }
  }, [showDrawer, startInEditMode]);

  useEffect(() => {
    setStatus(selectedID?.status || "");
  }, [selectedID]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => setShowDrawer(false), 250);
  };

  const statusOptions = useMemo(
    () => [
      { label: "Activate", value: "ACTIVE" },
      { label: "Suspend", value: "SUSPENDED" },
      { label: "Block", value: "BLOCKED" },
    ],
    []
  );

  const currentStatusValue = isEditingMode ? status : selectedID?.status;
  const statusPanelStyle = getStatusPanelStyle(currentStatusValue);

  const handleSaveChanges = async () => {
    if (!selectedID?.id || !status || !updateClientID) return;

    try {
      setIsSubmitting(true);

      await updateClientID(selectedID.id, {
        status,
        updatedBy: user?.id,
        updatedAt: new Date().toISOString(),
      });

      setIsEditingMode(false);
      onExitEdit?.();
    } catch (err) {
      toast.error("Failed to update client ID");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ============================= */
  /* RENDER                        */
  /* ============================= */

  return (
    <Offcanvas
      show={visible}
      onHide={handleClose}
      placement="end"
      className="border-0"
      style={{ width: "32rem", maxWidth: "100vw" }}
    >
      <style>
        {`
          @keyframes statusDotBlink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.35; transform: scale(0.85); }
          }
        `}
      </style>
      {/* Header */}
      <div
        className="px-4 py-3"
        style={{
          borderBottom: "1px solid #d8e2ef",
          backgroundColor: "#f9fbfd",
        }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="fw-bold mb-1">Client ID Details</h5>
            <div className="text-600" style={{ fontSize: 12 }}>
              Identifier metadata and lifecycle status
            </div>
          </div>
          <CloseButton onClick={handleClose} />
        </div>
      </div>

      {/* Body */}
      <div
        className="flex-grow-1 overflow-auto px-4 py-4"
        style={{ backgroundColor: "#f9fbfd" }}
      >
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Spinner animation="border" variant="success" />
          </div>
        ) : selectedID ? (
          <div
            className="bg-white rounded-4 p-4"
            style={{
              border: "1px solid #d8e2ef",
              boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
            }}
          >
            {/* Status Section */}
            <div
              className="mb-4 p-3 rounded-3"
              style={{
                backgroundColor: statusPanelStyle.bg,
                border: `1px solid ${statusPanelStyle.border}`,
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div
                  className="text-uppercase fw-semibold"
                  style={{
                    fontSize: 11,
                    letterSpacing: 0.6,
                    color: statusPanelStyle.heading,
                  }}
                >
                  Status
                </div>
                <span className="text-500" style={{ fontSize: 11 }}>
                  Editable
                </span>
              </div>

              {isEditingMode ? (
                <Select
                  size="middle"
                  style={{ width: "100%", maxWidth: 240 }}
                  value={status || undefined}
                  placeholder="Select status"
                  options={statusOptions}
                  onChange={setStatus}
                />
              ) : (
                <StatusPill status={selectedID?.status} />
              )}
            </div>

            {/* Metadata Section */}
            <DetailItem label="Client ID">
              <CopyableText value={selectedID.id} />
            </DetailItem>

            <DetailItem label="API Key">
              <CopyableText value={selectedID.apiKeyId} />
            </DetailItem>

            <DetailItem label="Created At">
              {formatDateTime(selectedID.createdAt)}
            </DetailItem>

            <DetailItem label="Updated At">
              {formatDateTime(selectedID.updatedAt)}
            </DetailItem>

            <DetailItem label="Updated By">
              {selectedID.updatedBy}
            </DetailItem>
          </div>
        ) : (
          <div className="text-center text-600 py-5">
            No client details available.
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="border-top px-4 py-3"
        style={{
          borderColor: "#d8e2ef",
          backgroundColor: "#fff",
        }}
      >
        {!isEditingMode ? (
          <div className="d-flex justify-content-between align-items-center">
            <AntButton
              type="primary"
              className="bg-success border-0 text-white"
              onClick={() => {
                setIsEditingMode(true);
                onRequestEdit?.();
              }}
              icon={<FontAwesomeIcon icon={faEdit} />}
            >
              Edit Status
            </AntButton>

            <AntButton
              className="bg-secondary text-white border-0 text-xs fw-bold"
              onClick={handleClose}
            >
              Close
            </AntButton>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-center">
            <AntButton
              type="primary"
              className="bg-success border-0 text-white"
              loading={isSubmitting}
              disabled={!status}
              onClick={handleSaveChanges}
            >
              Save Changes
            </AntButton>

            <AntButton
              onClick={() => {
                setStatus(selectedID?.status || "");
                setIsEditingMode(false);
                onExitEdit?.();
              }}
            >
              Cancel
            </AntButton>
          </div>
        )}
      </div>
    </Offcanvas>
  );
};
